import { useState, useCallback } from 'react';
import clsx from 'clsx';
import buyTrainingLink from '@/domain/buyTrainingLink';
import ClassDates from './ClassDates';
import ClassBadge from './ClassBadge';

export function ClassInfoPanelSection({
  title,
  children,
}) {
  return (
    <div className="flex flex-col space-y-1 py-4 lg:py-6">
      <h5 className="font-semibold">
        {title}
      </h5>
      {children}
    </div>
  );
}

export function ClassInfoPanelDates({
  dates,
}) {
  if (dates === undefined || dates === null || dates.length === 0) {
    return null;
  }

  const [isOpen, setOpen] = useState(false);

  const toggleDates = useCallback(() => {
    setOpen((isOpen) => !isOpen);
  }, [isOpen]);
  
  const moreThanThreeDates = dates.length > 3;

  return (
    <ClassInfoPanelSection title="Fechas">
      <div
        className={clsx(
          '-mx-6 -my-4 px-2 relative border-t border-b',
          {
            'h-28': moreThanThreeDates,
            'overflow-hidden border-transparent': moreThanThreeDates && isOpen === false,
            'overflow-auto border-gray-200 bg-gray-100 scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-300': moreThanThreeDates && isOpen === true,
          }
        )}
      >
        <ClassDates dates={dates} />
        {(moreThanThreeDates && isOpen === false) && (
          <div className="bg-gradient-to-t from-white to-transparent absolute w-full left-0 right-0 bottom-0 h-20" />
        )}
      </div>
      {moreThanThreeDates && (
        <div className="flex justify-start pt-4">
          <button onClick={toggleDates} className="font-medium focus:outline-none hover:text-green-500 focus:ring-2 focus:ring-gray-900 px-3 py-1 inline-flex leading-5 rounded-full focus:bg-gray-900 focus:text-white" type="button">
            {isOpen ? 'Ocultar fechas' : 'Mostrar fechas'}
          </button>
        </div>
      )}
    </ClassInfoPanelSection>
  );
}

function ClassProductDetails({
  product,
}) {
  return (
    <div className="flex items-center space-y-1 py-4">
      <h5 className="font-semibold m-0 flex-1">
        Inversión
      </h5>
      <div className="text-2xl font-medium font-mono text-green-600">
        {product.price}
      </div>
    </div>
  );
}

function ClassRegisterLink({
  product,
}) {
  return (
    <div className="py-6 bg-gray-100 px-6 rounded-b-lg lg:rounded-b-xl xl:rounded-b-2xl w-full flex flex-col">
      <a className="bg-green-500 hover:bg-green-600 focus:ring-2 focus:ring-green-600 focus:bg-green-600 text-white inline-flex px-6 py-2 rounded-full font-medium text-center justify-center focus:outline-none transition ease-in-out duration-100" href={buyTrainingLink(product.databaseId)} target="_blank" rel="noopener noreferrer">
        Regístrate en este curso
      </a>
    </div>
  );
}

export default function ClassInfoPanel({
  classDetails,
}) {
  if (classDetails === null) {
    return null;
  }

  const hasLocation = classDetails.location !== null;
  const hasProduct = classDetails.product !== null;

  return (
    <div className="bg-white rounded-lg lg:rounded-xl xl:rounded-2xl shadow-xl overflow-hidden">
      <div className="bg-gray-900 rounded-t px-6 py-4 flex items-center space-x-4">
        <h4 className="text-white font-medium text-lg">
          Detalles del curso
        </h4>
      </div>
      <div className="divide-y px-6">
        {hasProduct && (
          <ClassProductDetails product={classDetails.product} />
        )}
        <div className="flex items-center space-y-1 py-4">
          <h5 className="font-semibold m-0 flex-1">
            Modalidad
          </h5>
          <ClassBadge type={classDetails.type} roundedFull />
        </div>
        {hasLocation && (
          <div className="flex flex-col space-y-3 py-4">
            <h5 className="font-normal flex-1 flex items-start space-x-2">
              <svg className="w-5 h-5 text-gray-300 mt-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <div className="text-lg flex-1">
                {classDetails.location}
              </div>
            </h5>
          </div>
        )}
      </div>
      {hasProduct && (<ClassRegisterLink product={classDetails.product} />)}
    </div>
  );
}
